import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, Loader2 } from "lucide-react";
import { COMPANY } from "@/lib/pricing";
import api from "@/lib/api";
import { toast } from "sonner";

const EMAIL_RE = /^\S+@\S+\.\S+$/;

// Built-in finance director — always available for one-click internal sends.
const FINANCE_DIRECTOR = { name: "Stephen Bell (Finance Director)", email: "financemanager@kgmowers.com" };

// Internal review: send the current quote to sales agents and/or the finance director.
export const TeamSend = ({ html, attachPdf }) => {
  const [open, setOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [extra, setExtra] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && agents.length === 0)
      api
        .get("/sales-agents")
        .then(({ data }) => {
          const withoutFd = data.filter(
            (a) => (a.email || "").toLowerCase() !== FINANCE_DIRECTOR.email
          );
          setAgents([{ id: "finance-director", ...FINANCE_DIRECTOR }, ...withoutFd]);
        })
        .catch(() => setAgents([{ id: "finance-director", ...FINANCE_DIRECTOR }]));
  }, [open, agents.length]);

  const toggle = (email) =>
    setSelected((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

  const recipients = [
    ...new Set([...selected, ...extra.split(",").map((e) => e.trim()).filter((e) => EMAIL_RE.test(e))]),
  ];

  const send = async () => {
    if (!recipients.length) {
      toast.error("Pick an agent or type the finance director's email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/send-rfp", {
        recipient_emails: recipients,
        subject: `Internal review — Delivery Quote — ${COMPANY.name}`,
        html_content: html,
        reply_to: COMPANY.rfpEmail,
        cc_owner: false,
        attach_pdf: attachPdf,
        base_url: window.location.origin,
        pdf_filename: "KG-Delivery-Quote.pdf",
      });
      toast.success(data.message || `Sent to ${recipients.length} team member(s)`);
      setOpen(false);
      setSelected([]);
      setExtra("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send to team");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-[#1C1917]/30 font-bold"
        data-testid="team-send-open-button"
      >
        <Users className="h-3.5 w-3.5 mr-1.5" /> Send to Team
      </Button>
    );
  }

  return (
    <div className="w-full rounded-md border border-dashed border-[#1C1917]/30 bg-white p-3 space-y-2" data-testid="team-send-panel">
      <Label className="text-[11px] font-bold uppercase text-[#57534E]">
        Send to in-house team (sales agents / finance director)
      </Label>
      {agents.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {agents.map((a, i) => (
            <label
              key={a.id}
              className={`flex items-center gap-1.5 rounded-md border px-2 py-1 cursor-pointer text-xs ${
                selected.includes(a.email) ? "border-[#F97316] bg-[#FFF7ED]" : "border-border"
              }`}
              data-testid={`team-agent-${i}`}
            >
              <Checkbox
                checked={selected.includes(a.email)}
                onCheckedChange={() => toggle(a.email)}
                className="h-3.5 w-3.5 data-[state=checked]:bg-[#F97316] data-[state=checked]:border-[#F97316]"
              />
              <span className="font-medium text-[#1C1917]">{a.name}</span>
              <span className="text-[#57534E]">{a.email}</span>
            </label>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="text"
          placeholder="finance@kgmowers.com (comma-separate for more)"
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          className="flex-1 min-w-[220px] h-8 text-sm focus-visible:ring-2 focus-visible:ring-[#F97316]/30"
          data-testid="team-send-extra-emails"
        />
        <Button
          size="sm"
          onClick={send}
          disabled={sending}
          className="bg-[#1C1917] hover:bg-black text-white h-8"
          data-testid="team-send-button"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Send{recipients.length ? ` (${recipients.length})` : ""}</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-8" data-testid="team-send-cancel">
          Cancel
        </Button>
      </div>
    </div>
  );
};
